import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { 
  Search, 
  MapPin, 
  Star, 
  Heart,
  Map,
  Compass,
  BookOpen,
  User,
  ChevronRight,
  ChevronDown,
  Clock,
  Navigation,
  TreePine,
  Building2,
  Utensils,
  Camera,
  Loader2,
  Store,
  Tag,
  Users,
  Home,
  CalendarDays,
  Bell,
  Settings,
  X,
  Shield,
  Moon,
  LogOut,
  MessageSquare,
  HelpCircle,
  SlidersHorizontal,
  Mountain,
  Waves,
  Church,
  Eye,
  Droplets,
  Car,
  Footprints,
  Route
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { placesApi, routesApi, favoritesApi, notificationsApi } from '../services/api';
import MapPage from './MapPage';
import RoutesPage from './RoutesPage';
import FavoritesPage from './FavoritesPage';
import ProfilePage from './ProfilePage';
import BusinessesPage from './BusinessesPage';
import CampaignsPage from './CampaignsPage';
import CommunityPage from './CommunityPage';
import HomePage from './HomePage';
import MyTripsPage from './MyTripsPage';
import PlaceDetailPage from './PlaceDetailPage';
import BusinessOwnerPanel from './BusinessOwnerPanel';
import MiniMapPreview from '../components/MiniMapPreview';

const ExplorePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, isAuthenticated, updateFavorites } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'home');
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Data states
  const [featuredPlaces, setFeaturedPlaces] = useState([]);
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedPlaces, setLikedPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  
  // Overlay panels state
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [settingsView, setSettingsView] = useState('main'); // main, editProfile, accountSettings, privacy, help
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  
  // District selection state
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  
  // Filter states
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    rating: 'all',
    entranceFee: 'all',
    duration: 'all',
    sortBy: 'popular'
  });
  
  // Filter options
  const placeCategories = [
    { id: 'all', name: 'Tümü', icon: Compass },
    { id: 'tarihi', name: 'Tarihi Yerler', icon: Building2 },
    { id: 'doga', name: 'Doğal Güzellikler', icon: TreePine },
    { id: 'plaj', name: 'Plajlar & Koylar', icon: Waves },
    { id: 'yayla', name: 'Yaylalar', icon: Mountain },
    { id: 'selale', name: 'Şelaleler', icon: Droplets },
    { id: 'muze', name: 'Müzeler', icon: Building2 },
    { id: 'dini', name: 'Dini Mekanlar', icon: Church },
    { id: 'manzara', name: 'Manzara Noktaları', icon: Eye },
  ];
  
  const ratingOptions = [
    { id: 'all', name: 'Tümü' },
    { id: '4', name: '4+ Yıldız' },
    { id: '3', name: '3+ Yıldız' },
  ];
  
  const entranceFeeOptions = [
    { id: 'all', name: 'Tümü' },
    { id: 'free', name: 'Ücretsiz' },
    { id: 'paid', name: 'Ücretli' },
  ];
  
  const durationOptions = [
    { id: 'all', name: 'Tümü' },
    { id: 'short', name: '1 saatten az' },
    { id: 'medium', name: '1-3 saat' },
    { id: 'halfday', name: 'Yarım gün' },
    { id: 'fullday', name: 'Tam gün' },
  ];
  
  const sortOptions = [
    { id: 'popular', name: 'En Popüler' },
    { id: 'rating', name: 'En Yüksek Puan' },
    { id: 'newest', name: 'Yeni Eklenen' },
  ];
  
  // Giresun districts
  const districts = [
    { id: 'all', name: 'Tüm Giresun' },
    { id: 'merkez', name: 'Merkez' },
    { id: 'bulancak', name: 'Bulancak' },
    { id: 'espiye', name: 'Espiye' },
    { id: 'gorele', name: 'Görele' },
    { id: 'tirebolu', name: 'Tirebolu' },
    { id: 'kesap', name: 'Keşap' },
    { id: 'eynesil', name: 'Eynesil' },
    { id: 'piraziz', name: 'Piraziz' },
    { id: 'dereli', name: 'Dereli' },
    { id: 'yaglidere', name: 'Yağlıdere' },
    { id: 'dogankent', name: 'Doğankent' },
    { id: 'guce', name: 'Güce' },
    { id: 'sebinkarahisar', name: 'Şebinkarahisar' },
    { id: 'alucra', name: 'Alucra' },
    { id: 'camoluk', name: 'Çamoluk' },
    { id: 'canakci', name: 'Çanakçı' },
  ];

  const iconMap = {
    Compass: Compass,
    Building2: Building2,
    TreePine: TreePine,
    Utensils: Utensils,
    Camera: Camera,
  };

  const categories = [
    { id: 'all', name: 'Tümü', icon: 'Compass' },
    { id: 'historic', name: 'Tarihi', icon: 'Building2' },
    { id: 'nature', name: 'Doğa', icon: 'TreePine' },
    { id: 'food', name: 'Yeme-İçme', icon: 'Utensils' },
    { id: 'photo', name: 'Fotoğraf', icon: 'Camera' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDistrictDropdown && !event.target.closest('.district-dropdown-container')) {
        setShowDistrictDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDistrictDropdown]);

  // Fetch data on mount and when district changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [placesData, routesData] = await Promise.all([
          placesApi.getFeatured(),
          routesApi.getPopular()
        ]);
        
        // Ensure data is array
        const safePlacesData = Array.isArray(placesData) ? placesData : [];
        const safeRoutesData = Array.isArray(routesData) ? routesData : [];
        
        // Filter by district if selected
        let filteredPlaces = safePlacesData;
        if (selectedDistrict !== 'all') {
          const districtName = districts.find(d => d.id === selectedDistrict)?.name?.toLowerCase();
          filteredPlaces = safePlacesData.filter(place => {
            const location = (place.location || place.address || '').toLowerCase();
            const name = (place.name || '').toLowerCase();
            return location.includes(districtName) || name.includes(districtName);
          });
        }
        
        setFeaturedPlaces(filteredPlaces);
        setPopularRoutes(safeRoutesData);
        
        // Set initial liked places from user data
        if (user?.favorites && Array.isArray(user.favorites)) {
          setLikedPlaces(user.favorites);
        } else {
          setLikedPlaces([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setFeaturedPlaces([]);
        setPopularRoutes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, selectedDistrict]);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await notificationsApi.getUnreadCount();
        setUnreadNotifications(data?.count || 0);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };
    fetchUnreadCount();
  }, [isAuthenticated]);

  // Fetch notifications for panel
  const fetchNotificationsForPanel = async () => {
    if (!isAuthenticated) return;
    setNotificationsLoading(true);
    try {
      const data = await notificationsApi.getNotifications();
      const safeData = Array.isArray(data) ? data : [];
      setNotifications(safeData);
      setUnreadNotifications(safeData.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Open notifications panel
  const openNotifications = () => {
    setShowSettingsPanel(false); // Close settings if open
    setShowNotificationsPanel(true);
    fetchNotificationsForPanel();
  };

  // Open settings panel overlay
  const openSettings = () => {
    setShowNotificationsPanel(false); // Close notifications if open
    setShowSettingsPanel(true);
  };

  // Mark notification as read
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await notificationsApi.markAsRead(notification.id);
        setNotifications(prev => (Array.isArray(prev) ? prev : []).map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        ));
        setUnreadNotifications(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    // Navigate based on type
    setShowNotificationsPanel(false);
    if (notification.type === 'follow' || notification.type === 'like' || notification.type === 'comment') {
      setActiveTab('community');
    }
  };

  // Mark all as read
  const markAllNotificationsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => (Array.isArray(prev) ? prev : []).map(n => ({ ...n, read: true })));
      setUnreadNotifications(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Time ago helper
  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  // Search functionality
  useEffect(() => {
    const searchPlaces = async () => {
      if (searchQuery.length > 2) {
        try {
          const results = await placesApi.getAll(activeCategory, searchQuery);
          setFeaturedPlaces(Array.isArray(results) ? results : []);
        } catch (error) {
          console.error('Search error:', error);
          setFeaturedPlaces([]);
        }
      } else if (searchQuery.length === 0) {
        const data = await placesApi.getFeatured();
        setFeaturedPlaces(Array.isArray(data) ? data : []);
      }
    };

    const debounce = setTimeout(searchPlaces, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, activeCategory]);

  // Category filter
  useEffect(() => {
    const filterByCategory = async () => {
      try {
        if (activeCategory === 'all') {
          const data = await placesApi.getFeatured();
          setFeaturedPlaces(Array.isArray(data) ? data : []);
        } else {
          const cat = categories.find(c => c.id === activeCategory);
          const data = await placesApi.getAll(cat?.slug);
          setFeaturedPlaces(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Filter error:', error);
        setFeaturedPlaces([]);
      }
    };

    if (!searchQuery) {
      filterByCategory();
    }
  }, [activeCategory]);

  const toggleLike = async (placeId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const result = await favoritesApi.toggle(placeId);
      
      if (result.is_favorite) {
        const newLikes = [...likedPlaces, placeId];
        setLikedPlaces(newLikes);
        updateFavorites(newLikes);
      } else {
        const newLikes = likedPlaces.filter(id => id !== placeId);
        setLikedPlaces(newLikes);
        updateFavorites(newLikes);
      }
    } catch (error) {
      console.error('Toggle favorite error:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Protected tabs that require authentication
  const protectedTabs = ['mytrips', 'routes', 'community', 'profile', 'favorites', 'business-panel'];

  // Handle tab change from child components
  const handleTabChange = (tab) => {
    // Check if tab requires authentication
    if (protectedTabs.includes(tab) && !isAuthenticated) {
      navigate('/login');
      return;
    }
    setActiveTab(tab);
    setSelectedPlace(null); // Clear selected place when changing tabs
  };

  // Handle place click
  const handlePlaceClick = (place) => {
    setSelectedPlace(place);
  };

  // Handle back from detail page
  const handleBackFromDetail = () => {
    setSelectedPlace(null);
  };

  // Render content based on active tab
  const renderContent = () => {
    // If a place is selected, show detail page
    if (selectedPlace) {
      return (
        <PlaceDetailPage 
          place={selectedPlace} 
          onBack={handleBackFromDetail}
          onNavigateToPlace={handlePlaceClick}
        />
      );
    }

    switch (activeTab) {
      case 'home':
        return <HomePage onTabChange={handleTabChange} selectedDistrict={selectedDistrict} districts={districts} />;
      case 'map':
        return <MapPage />;
      case 'routes':
        return <RoutesPage />;
      case 'favorites':
        return <FavoritesPage />;
      case 'profile':
        return <ProfilePage onTabChange={handleTabChange} />;
      case 'businesses':
        return <BusinessesPage onTabChange={handleTabChange} />;
      case 'campaigns':
        return <CampaignsPage onTabChange={handleTabChange} />;
      case 'community':
        return <CommunityPage onTabChange={handleTabChange} />;
      case 'mytrips':
        return <MyTripsPage onTabChange={handleTabChange} />;
      case 'business-panel':
        return <BusinessOwnerPanel onBack={() => setActiveTab('profile')} />;
      default:
        return renderExploreContent();
    }
  };

  const renderExploreContent = () => {
    // Dark mode classes for explore content
    const cardBg = darkMode ? 'bg-slate-800' : 'bg-white';
    const cardText = darkMode ? 'text-white' : 'text-gray-900';
    const cardTextSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
    const cardBorder = darkMode ? 'border-slate-700' : 'border-gray-200';
    const inputBg = darkMode ? 'bg-slate-800' : 'bg-white';

    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      );
    }

    return (
      <main className="max-w-3xl mx-auto px-4 py-4 pb-24">
        {/* Location, Search Bar and Filter - All in one row */}
        <div className="flex gap-2 mb-4">
          {/* District Selector */}
          <div className="relative district-dropdown-container">
            <button 
              onClick={() => setShowDistrictDropdown(!showDistrictDropdown)}
              className={`flex items-center gap-2 px-3 h-12 ${cardBg} rounded-xl border ${cardBorder} shadow-sm whitespace-nowrap`}
            >
              <MapPin className={`w-5 h-5 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
              <span className={`font-medium ${cardText} text-sm`}>
                {selectedDistrict === 'all' ? 'Tüm Giresun' : districts.find(d => d.id === selectedDistrict)?.name}
              </span>
              <ChevronDown className={`w-4 h-4 ${cardTextSecondary} transition-transform ${showDistrictDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {/* District Dropdown */}
            {showDistrictDropdown && (
              <div className={`absolute top-full left-0 mt-2 w-56 ${cardBg} rounded-xl shadow-xl border ${cardBorder} py-2 z-50 max-h-80 overflow-y-auto`}>
                {districts.map((district) => (
                  <button
                    key={district.id}
                    onClick={() => {
                      setSelectedDistrict(district.id);
                      setShowDistrictDropdown(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left flex items-center gap-3 ${
                      selectedDistrict === district.id 
                        ? darkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-50 text-teal-700'
                        : darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'
                    } transition-colors`}
                  >
                    <MapPin className={`w-4 h-4 ${selectedDistrict === district.id ? 'text-teal-500' : cardTextSecondary}`} />
                    <span className={`text-sm font-medium ${selectedDistrict === district.id ? '' : cardText}`}>
                      {district.name}
                    </span>
                    {selectedDistrict === district.id && (
                      <span className="ml-auto text-teal-500">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${cardTextSecondary}`} />
            <Input
              type="text"
              placeholder="Mekan ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-12 pr-4 py-3 h-12 rounded-xl ${cardBorder} ${inputBg} shadow-sm w-full ${cardText}`}
            />
          </div>
          <button
            onClick={() => setShowFilterPanel(true)}
            className={`h-12 px-4 ${cardBg} rounded-xl border ${cardBorder} shadow-sm flex items-center gap-2 ${cardText} hover:shadow-md transition-shadow`}
          >
            <SlidersHorizontal className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">Filtrele</span>
          </button>
        </div>

        {/* Active Filters Display */}
        {(filters.category !== 'all' || filters.rating !== 'all' || filters.entranceFee !== 'all' || filters.duration !== 'all') && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.category !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-50 text-teal-700'} flex items-center gap-1`}>
                {placeCategories.find(c => c.id === filters.category)?.name}
                <button onClick={() => setFilters(f => ({...f, category: 'all'}))} className="ml-1">×</button>
              </span>
            )}
            {filters.rating !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-700'} flex items-center gap-1`}>
                {ratingOptions.find(r => r.id === filters.rating)?.name}
                <button onClick={() => setFilters(f => ({...f, rating: 'all'}))} className="ml-1">×</button>
              </span>
            )}
            {filters.entranceFee !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'} flex items-center gap-1`}>
                {entranceFeeOptions.find(e => e.id === filters.entranceFee)?.name}
                <button onClick={() => setFilters(f => ({...f, entranceFee: 'all'}))} className="ml-1">×</button>
              </span>
            )}
            {filters.duration !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-700'} flex items-center gap-1`}>
                {durationOptions.find(d => d.id === filters.duration)?.name}
                <button onClick={() => setFilters(f => ({...f, duration: 'all'}))} className="ml-1">×</button>
              </span>
            )}
            <button 
              onClick={() => setFilters({ category: 'all', rating: 'all', entranceFee: 'all', duration: 'all', sortBy: 'popular' })}
              className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}
            >
              Temizle
            </button>
          </div>
        )}

        {/* Categories */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((cat) => {
            const Icon = iconMap[cat.icon] || Compass;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat.id
                    ? 'bg-[#1e3a5f] text-white shadow-md'
                    : `${cardBg} ${cardTextSecondary} ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} border ${cardBorder}`
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{cat.name}</span>
              </button>
            );
          })}
        </div>

        {/* Featured Section */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-bold ${cardText}`}>
              {activeCategory === 'all' ? 'Öne Çıkan Yerler' : categories.find(c => c.id === activeCategory)?.name}
            </h2>
            <button className="flex items-center gap-1 text-[#1e3a5f] text-sm font-medium hover:underline">
              Tümünü Gör
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.isArray(featuredPlaces) && featuredPlaces.length > 0 ? (
              featuredPlaces
                .filter(place => {
                  if (activeCategory === 'all') return true;
                  const placeCat = (place.category_name || place.category || '').toLowerCase();
                  // Map category IDs to category patterns
                  switch(activeCategory) {
                    case 'historic':
                      return placeCat.includes('tarihi');
                    case 'nature':
                      return placeCat.includes('doğal') || placeCat.includes('doğa') || placeCat.includes('şelale');
                    case 'food':
                      return placeCat.includes('yeme') || placeCat.includes('restoran') || placeCat.includes('kafe');
                    case 'photo':
                      return placeCat.includes('fotoğraf') || placeCat.includes('manzara');
                    default:
                      return true;
                  }
                })
                .map((place) => (
                <div 
                  key={place.id}
                  onClick={() => handlePlaceClick(place)}
                  className={`${cardBg} rounded-2xl overflow-hidden border ${cardBorder} hover:border-teal-500/30 hover:shadow-2xl hover:shadow-teal-900/5 transition-all duration-300 group cursor-pointer flex flex-col h-full`}
                >
                  {/* Top Image Area */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={place.image} 
                      alt={place.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80';
                      }}
                    />
                    
                    {/* Tags */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                       <span className="px-3 py-1 bg-teal-500 text-white text-xs font-bold rounded-md shadow-sm">
                         Öne Çıkan
                       </span>
                       {(place.category_name || place.category) && (
                         <span className="px-3 py-1 bg-slate-900/80 backdrop-blur text-white text-xs font-medium rounded-md shadow-sm w-fit capitalize">
                           {place.category_name || place.category}
                         </span>
                       )}
                    </div>
                    
                    {/* Like Button */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLike(place.id);
                      }}
                      className="absolute top-4 right-4 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-slate-50 transition-colors"
                    >
                      <Heart className={`w-5 h-5 ${likedPlaces.includes(place.id) ? 'fill-red-500 text-red-500' : 'text-slate-400'}`} />
                    </button>
                  </div>
                  
                  {/* Content Area */}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1 bg-yellow-100/50 px-2 py-1 rounded text-yellow-700">
                        <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
                        <span className="text-xs font-bold">{place.rating || '4.5'}</span>
                        <span className="text-xs text-yellow-600/70 ml-1">({Math.floor(Math.random() * 50) + 10})</span>
                      </div>
                      <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                        Ziyarete Açık
                      </span>
                    </div>
                    
                    <h3 className={`text-xl font-bold ${cardText} mb-2 group-hover:text-teal-600 transition-colors line-clamp-1`}>
                      {place.name}
                    </h3>
                    
                    <div className={`flex items-start gap-1.5 ${cardTextSecondary} text-sm mb-4 line-clamp-2 flex-1`}>
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{place.location || place.address || 'Giresun Merkez'}</span>
                    </div>
                    
                    {/* Footer divider */}
                    <div className={`w-full h-px ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} mb-4`}></div>
                    
                    {/* Authors / Meta */}
                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-teal-100 border-2 border-white flex items-center justify-center overflow-hidden">
                          <span className="text-teal-700 font-bold text-xs">GK</span>
                        </div>
                        <span className={`text-sm font-medium ${cardText}`}>Rehber</span>
                      </div>
                      
                      <div className={`flex items-center gap-1 ${cardTextSecondary} text-sm`}>
                         <Car className="w-4 h-4" />
                         <span>Araçla gidilebilir</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-8 text-gray-500">
                Henüz öne çıkan yer bulunamadı.
              </div>
            )}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="mb-8">
          <h2 className={`text-xl font-bold ${cardText} mb-4`}>Hızlı Erişim</h2>
          <div className="grid grid-cols-1 gap-3">
            <button 
              onClick={() => setActiveTab('map')}
              className="bg-gradient-to-br from-[#1e3a5f] to-[#2a4a73] text-white rounded-2xl p-5 text-left hover:shadow-lg transition-shadow"
            >
              <Map className="w-7 h-7 mb-2" />
              <h3 className="font-semibold text-sm mb-0.5">Harita</h3>
              <p className="text-xs text-white/80">Tüm mekanları görün</p>
            </button>
          </div>
        </section>
      </main>
    );
  };

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className={`min-h-screen ${bgMain} flex flex-col`}>
      {/* Header (Instagram Style) */}
      <header className={`${bgCard} border-b ${borderColor} sticky top-0 z-[9999]`}>
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <h1 className={`text-2xl font-black italic tracking-tighter ${textPrimary}`}>
                GiresunKent
              </h1>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-5">
              {isAuthenticated ? (
                <>
                  {/* Notifications (Heart) */}
                  <button 
                    onClick={openNotifications}
                    className="relative transition-transform hover:scale-110 active:scale-95"
                    title="Bildirimler"
                  >
                    <Heart className={`w-6 h-6 ${textPrimary}`} />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-800">
                        {unreadNotifications > 99 ? '99+' : unreadNotifications}
                      </span>
                    )}
                  </button>
                  {/* Messages / Direct */}
                  <button 
                    onClick={openSettings}
                    className="relative transition-transform hover:scale-110 active:scale-95"
                    title="Mesajlar ve Ayarlar"
                  >
                    <MessageSquare className={`w-6 h-6 ${textPrimary}`} />
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="px-4 py-1.5 rounded-lg bg-teal-500 text-white text-sm font-bold shadow-sm"
                  title="Giriş Yap"
                >
                  Giriş Yap
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-auto" style={{ height: 'calc(100vh - 140px)', paddingBottom: '80px' }}>
        {renderContent()}
      </div>

      {/* Filter Panel Overlay */}
      {showFilterPanel && (
        <div className="fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowFilterPanel(false)}
          />
          {/* Panel */}
          <div className={`absolute top-0 right-0 w-full max-w-md h-full ${bgMain} shadow-xl overflow-hidden flex flex-col`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
              <h2 className={`text-lg font-bold ${textPrimary}`}>Filtreler</h2>
              <button 
                onClick={() => setShowFilterPanel(false)}
                className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
              >
                <X className={`w-5 h-5 ${textSecondary}`} />
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Category Filter */}
              <div>
                <h3 className={`font-semibold ${textPrimary} mb-3`}>Kategori</h3>
                <div className="grid grid-cols-2 gap-2">
                  {placeCategories.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setFilters(f => ({...f, category: cat.id}))}
                        className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${
                          filters.category === cat.id
                            ? darkMode ? 'bg-teal-900/30 border-teal-500 text-teal-400' : 'bg-teal-50 border-teal-500 text-teal-700'
                            : `${bgCard} ${borderColor} ${textPrimary} hover:border-teal-300`
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Rating Filter */}
              <div>
                <h3 className={`font-semibold ${textPrimary} mb-3`}>Puan</h3>
                <div className="flex gap-2">
                  {ratingOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setFilters(f => ({...f, rating: opt.id}))}
                      className={`flex-1 flex items-center justify-center gap-1 p-3 rounded-xl border transition-all ${
                        filters.rating === opt.id
                          ? darkMode ? 'bg-yellow-900/30 border-yellow-500 text-yellow-400' : 'bg-yellow-50 border-yellow-500 text-yellow-700'
                          : `${bgCard} ${borderColor} ${textPrimary} hover:border-yellow-300`
                      }`}
                    >
                      {opt.id !== 'all' && <Star className="w-4 h-4 fill-current" />}
                      <span className="text-sm font-medium">{opt.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Entrance Fee Filter */}
              <div>
                <h3 className={`font-semibold ${textPrimary} mb-3`}>Giriş Ücreti</h3>
                <div className="flex gap-2">
                  {entranceFeeOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setFilters(f => ({...f, entranceFee: opt.id}))}
                      className={`flex-1 p-3 rounded-xl border transition-all text-center ${
                        filters.entranceFee === opt.id
                          ? darkMode ? 'bg-green-900/30 border-green-500 text-green-400' : 'bg-green-50 border-green-500 text-green-700'
                          : `${bgCard} ${borderColor} ${textPrimary} hover:border-green-300`
                      }`}
                    >
                      <span className="text-sm font-medium">{opt.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Duration Filter */}
              <div>
                <h3 className={`font-semibold ${textPrimary} mb-3`}>Ziyaret Süresi</h3>
                <div className="grid grid-cols-2 gap-2">
                  {durationOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setFilters(f => ({...f, duration: opt.id}))}
                      className={`flex items-center justify-center gap-1 p-3 rounded-xl border transition-all ${
                        filters.duration === opt.id
                          ? darkMode ? 'bg-purple-900/30 border-purple-500 text-purple-400' : 'bg-purple-50 border-purple-500 text-purple-700'
                          : `${bgCard} ${borderColor} ${textPrimary} hover:border-purple-300`
                      }`}
                    >
                      {opt.id !== 'all' && <Clock className="w-4 h-4" />}
                      <span className="text-sm font-medium">{opt.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Sort By */}
              <div>
                <h3 className={`font-semibold ${textPrimary} mb-3`}>Sıralama</h3>
                <div className="space-y-2">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setFilters(f => ({...f, sortBy: opt.id}))}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        filters.sortBy === opt.id
                          ? darkMode ? 'bg-blue-900/30 border-blue-500 text-blue-400' : 'bg-blue-50 border-blue-500 text-blue-700'
                          : `${bgCard} ${borderColor} ${textPrimary} hover:border-blue-300`
                      }`}
                    >
                      <span className="text-sm font-medium">{opt.name}</span>
                      {filters.sortBy === opt.id && <span className="text-teal-500">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className={`p-4 border-t ${borderColor} flex gap-3`}>
              <button
                onClick={() => {
                  setFilters({ category: 'all', rating: 'all', entranceFee: 'all', duration: 'all', sortBy: 'popular' });
                }}
                className={`flex-1 py-3 rounded-xl border ${borderColor} ${textPrimary} font-medium`}
              >
                Temizle
              </button>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="flex-1 py-3 rounded-xl bg-teal-500 text-white font-medium hover:bg-teal-600 transition-colors"
              >
                Uygula
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications and Settings Panels (Same as before) */}
      {/* ... (Existing Notifications and Settings Panels Code) ... */}

      {/* Bottom Navigation */}
      <nav className={`fixed bottom-0 left-0 right-0 ${bgCard} border-t ${borderColor} py-1 z-[9999] shadow-lg`}>
        <div className="max-w-3xl mx-auto flex items-center justify-between px-2 overflow-x-auto scrollbar-hide gap-1">
          {[
            { id: 'home', icon: Home, label: 'Akış' },
            { id: 'explore', icon: MapPin, label: 'Mekanlar' },
            { id: 'mytrips', icon: Navigation, label: 'Rotalar', protected: true },
            { id: 'businesses', icon: Store, label: 'Firmalar' },
            { id: 'campaigns', icon: Tag, label: 'Fırsatlar' },
            { id: 'community', icon: Users, label: 'Topluluk', protected: true },
            { id: 'profile', icon: User, label: 'Profil', protected: true },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.protected && !isAuthenticated) {
                    navigate('/login');
                    return;
                  }
                  setActiveTab(item.id);
                  setSelectedPlace(null);
                }}
                className={`flex flex-col items-center flex-shrink-0 gap-1 py-2 px-1 rounded-lg transition-colors min-w-[50px] ${
                  isActive
                    ? 'text-teal-600 dark:text-teal-400'
                    : darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5px]' : 'stroke-[1.5px]'}`} />
                <span className="text-[9px] font-bold leading-tight">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default ExplorePage;